// ────────────────────────────────────────────────────────────────────────
// Lógica pura del bloque de precisión de la vista Forecasting.
//
// El summary que devuelve el backend (forecasts_read.fetch_accuracy) sale del
// BACKTEST de la corrida vigente: el modelo de hoy evaluado contra lo realmente
// ocurrido en fechas que no vio al entrenar. Se reemplaza entero en cada corrida,
// así que no es un historial de corridas anteriores.
//
// Se muestra el WAPE (Σ|error| / Σreal) y no el MAPE: el MAPE promedia errores
// relativos por día y se dispara con días de volumen bajo (domingos, feriados),
// así que no describe el error típico de la operación.
//
// El sesgo (biasRel) NO se muestra a propósito: el backtest se evalúa sin la
// rampa estacional que sí lleva el punto servido, así que su sesgo (≈ −11% en
// created) no describe la proyección que el usuario tiene delante, y leerlo
// como "el modelo subestima" lleva a sobredimensionar. Volverá cuando el
// backend lo mida sobre lo servido.
// ────────────────────────────────────────────────────────────────────────
import { formatNumberCL, formatPercent } from "../../../utils/formatters";

/**
 * Historial de precisión de la serie elegida.
 *
 * Sin coincidencia devuelve null: en holding, caer a `history[0]` mostraba la
 * precisión de OTRA empresa como si fuera la elegida. Sin serie también devuelve
 * null, porque no hay entidad a la que atribuirle un historial (y en ese caso la
 * vista muestra el estado vacío, que no usa la precisión).
 *
 * El scopeId global llega como null; se trata igual que un scopeId ausente para
 * que una diferencia de serialización (null vs. sin campo) no esconda la
 * precisión de la serie global.
 *
 * @param {object[]} history  respuesta de getForecastHistory
 * @param {object|null} serie serie de proyección elegida
 * @returns {object|null}
 */
export function selectHistoryForSerie(history, serie) {
  if (!serie || !Array.isArray(history)) return null;
  const scopeId = serie.scopeId ?? null;
  return history.find((h) => (h?.scopeId ?? null) === scopeId) || null;
}

/**
 * Texto de apoyo del KPI de error típico: de dónde sale el dato.
 *
 * @param {object|null|undefined} summary { n, mae, mape, wape, biasRel }
 * @param {(key: string, opts?: object) => string} t
 * @returns {string}
 */
export function wapeHint(summary, t) {
  if (!summary?.n) return t("sin historial aún");
  return t("backtest de la corrida vigente");
}

/**
 * Subtítulo del panel de precisión: WAPE y MAE del backtest vigente.
 *
 * @param {object|null|undefined} summary { n, mae, mape, wape, biasRel }
 * @param {(key: string, opts?: object) => string} t
 * @returns {string}
 */
export function accuracySubtitle(summary, t) {
  if (!summary?.n) return t("Real vs. predicho en el backtest de la corrida vigente");
  return [
    t("Real vs. predicho"),
    `WAPE ${summary.wape != null ? formatPercent(summary.wape) : "—"}`,
    `MAE ${summary.mae != null ? formatNumberCL(Math.round(Number(summary.mae))) : "—"}`,
  ].join(" · ");
}
